import { z } from 'zod';
export const dashboardQuerySchema = z.object({
    scope: z.enum(['me', 'team']).default('me'),
    days: z.coerce.number().int().min(1).max(365).default(30),
});
/** A count/rate metric compared against the immediately-prior equal-length
 * window (design/orion-crm-design/screenshots/Dashboard.png's "▲8% vs prior
 * 30 days" style KPI deltas). `deltaPct` is null when `prior` is 0 (nothing
 * to take a percentage of). */
export const deltaMetricSchema = z.object({
    value: z.number(),
    prior: z.number(),
    delta: z.number(),
    deltaPct: z.number().nullable(),
});
/** Same shape as `deltaMetricSchema` but for currency amounts, which stay
 * decimal strings end-to-end (matching `openPipelineValue`/`wonValue`
 * elsewhere) rather than floats. */
export const moneyDeltaMetricSchema = z.object({
    value: z.string(),
    prior: z.string(),
    delta: z.string(),
    deltaPct: z.number().nullable(),
});
export const dashboardRepPerformanceRowSchema = z.object({
    membershipId: z.string().uuid(),
    displayName: z.string(),
    wonCount: z.number().int(),
    wonValue: z.string(),
    activityCount: z.number().int(),
});
/** design/orion-crm-design/screenshots/Dashboard.png "Needs your attention"
 * — real open/in-progress tasks the viewer owns or can see, not just a
 * count. `relatedName` resolves whichever of Task's lead/contact/company/
 * opportunity FK columns is set (those are informal references with no
 * Prisma relation, so DashboardService resolves them with a batched lookup
 * rather than a `include`). */
export const dashboardTaskRowSchema = z.object({
    id: z.string().uuid(),
    title: z.string(),
    dueAt: z.string().datetime().nullable(),
    isOverdue: z.boolean(),
    relatedName: z.string().nullable(),
    /** Which record `relatedName` came from, and enough to act on it directly
     * (design/orion-crm-design/rendered-html/MobileDashboard.html's "Next up"
     * row — tap-through, plus real Call/Email buttons). `relatedPersonName`
     * is the person's own name where `relatedName` prefers the company name
     * instead (a lead's `relatedName` is `companyName ?? fullName` for the
     * desktop widget's existing phrasing) — both are real, just answering two
     * different display needs from the same lookup. */
    relatedType: z.enum(['lead', 'contact', 'company', 'opportunity']).nullable(),
    relatedId: z.string().uuid().nullable(),
    relatedPersonName: z.string().nullable(),
    relatedPhone: z.string().nullable(),
    relatedEmail: z.string().nullable(),
    relatedIsHot: z.boolean(),
});
/** docs section 7 "Dashboard" — the KPI set. */
export const dashboardResponseSchema = z.object({
    totalLeads: deltaMetricSchema,
    newLeads: z.number().int(),
    newLeadsUnassigned: z.number().int(),
    qualifiedLeads: deltaMetricSchema,
    openDeals: z.number().int(),
    openPipelineValue: moneyDeltaMetricSchema,
    wonDeals: z.number().int(),
    wonValue: z.string(),
    lostDeals: z.number().int(),
    conversionRate: deltaMetricSchema,
    tasksDueToday: z.number().int(),
    tasksOverdue: z.number().int(),
    needsAttention: z.array(dashboardTaskRowSchema),
    weightedForecast: z.string(),
    avgDealSize: z.string(),
    avgDaysToClose: z.number().nullable(),
    pipelineByStage: z.array(z.object({ stageName: z.string(), count: z.number().int(), value: z.string() })),
    leadsBySource: z.array(z.object({ sourceName: z.string(), count: z.number().int() })),
    recentActivity: z.array(z.object({
        id: z.string().uuid(),
        kind: z.string(),
        occurredAt: z.string().datetime(),
        /** The bolded lead-in — the actor's first name, "You" when the
         * viewer did it themselves, or (for `won`, which the design phrases
         * entity-first with no actor at all) the deal name. */
        subject: z.string(),
        /** The rest of the line, already worded per `kind` server-side
         * (design/orion-crm-design/rendered-html/Dashboard.html uses a
         * different phrase shape per kind: "logged a call · X", "created
         * lead · X", "assigned X to Y", "won · $Z", "emailed X"). */
        description: z.string(),
    })),
    teamPerformance: z.array(dashboardRepPerformanceRowSchema),
});
//# sourceMappingURL=schemas.js.map